import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, shareReplay, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AUTH_TYPE, AuthTypes } from '../../common/interceptors/token.interceptor';
import { IFinding, IZone } from '../../common/models/detection.interface';
import {
    ICharts,
    ICompleteUploadRequest,
    ICreateJobRequest,
    IFindingsPage,
    IFindingsRequest,
    IIngestVmsRequest,
    IInitiateUploadRequest,
    IInitiateUploadResponse,
    IJobResults,
    ISummary,
    ISpotlyteJob,
    ISpotlyteJobDetail,
    ISpotlyteQnaTurn,
    ISpotlyteSource,
    mapCharts,
    mapCompleteUploadRequest,
    mapCreateJobRequest,
    mapIngestVmsRequests,
    mapFinding,
    mapFindingsPage,
    mapFindingsRequest,
    mapInitiateUploadRequest,
    mapJobResults,
    mapSpotlyteJob,
    mapSpotlyteJobDetail,
    mapSpotlyteQnaTurn,
    mapSpotlyteSource,
    mapSummary,
    mapZones,
} from '../../common/models/spotlyte.interface';

@Injectable({ providedIn: 'root' })
export class SpotlyteService {
    private http = inject(HttpClient);

    private static readonly PUBLIC_CONTEXT = new HttpContext().set(AUTH_TYPE, AuthTypes.PUBLIC);
    private blobCache = new Map<string, Observable<any>>();

    private static readonly endpoints = {
        sources: `${environment.apiUrl}/spotlyte/sources`,
        uploadInit: `${environment.apiUrl}/spotlyte/sources/upload/init`,
        uploadComplete: `${environment.apiUrl}/spotlyte/sources/upload/complete`,
        vmsIngest: `${environment.apiUrl}/spotlyte/sources/vms`,
        jobs: `${environment.apiUrl}/spotlyte/jobs`,
        job: (id: string) => `${environment.apiUrl}/spotlyte/jobs/${id}`,
        results: (id: string) => `${environment.apiUrl}/spotlyte/jobs/${id}/results`,
        exportCsv: (id: string) => `${environment.apiUrl}/spotlyte/jobs/${id}/export/csv`,
        exportPdf: (id: string) => `${environment.apiUrl}/spotlyte/jobs/${id}/export/pdf`,
        findings: `${environment.apiUrl}/spotlyte/findings`,
        qna: `${environment.apiUrl}/spotlyte/qna`,
        qnaHistory: (jobId: string) => `${environment.apiUrl}/spotlyte/jobs/${jobId}/qna`,
    };

    getSources(): Observable<ISpotlyteSource[]> {
        const ERROR = 'Failed to load sources';
        return this.http.get<any[]>(SpotlyteService.endpoints.sources).pipe(
            map(r => r.map(mapSpotlyteSource)),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    deleteSources(ids: string[]): Observable<ISpotlyteSource[]> {
        const ERROR = 'Failed to delete sources';
        return this.http.delete<any[]>(SpotlyteService.endpoints.sources, { body: { ids } }).pipe(
            map(r => r.map(mapSpotlyteSource)),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    initiateUpload(request: IInitiateUploadRequest): Observable<IInitiateUploadResponse> {
        const ERROR = 'Failed to initiate upload';
        return this.http.post<any>(SpotlyteService.endpoints.uploadInit, mapInitiateUploadRequest(request)).pipe(
            map(r => ({ sourceId: r.source_id, uploadUrl: r.upload_url })),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    ingestVmsSources(requests: IIngestVmsRequest[]): Observable<ISpotlyteSource[]> {
        const ERROR = 'Failed to ingest VMS sources';
        return this.http.post<any[]>(SpotlyteService.endpoints.vmsIngest, mapIngestVmsRequests(requests)).pipe(
            map(r => r.map(mapSpotlyteSource)),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    completeUpload(request: ICompleteUploadRequest): Observable<ISpotlyteSource> {
        const ERROR = 'Failed to complete upload';
        return this.http.post<any>(SpotlyteService.endpoints.uploadComplete, mapCompleteUploadRequest(request)).pipe(
            map(mapSpotlyteSource),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    getJobs(status?: string): Observable<ISpotlyteJob[]> {
        const ERROR = 'Failed to load jobs';
        const params: Record<string, string> = {};
        if (status) params['status'] = status;
        return this.http.get<any[]>(SpotlyteService.endpoints.jobs, { params }).pipe(
            map(r => r.map(mapSpotlyteJob)),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    getJob(jobId: string): Observable<ISpotlyteJobDetail> {
        const ERROR = `Failed to load job ${jobId}`;
        return this.http.get<any>(SpotlyteService.endpoints.job(jobId)).pipe(
            map(mapSpotlyteJobDetail),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    getResults(jobId: string): Observable<IJobResults> {
        const ERROR = `Failed to load results for job ${jobId}`;
        return this.http.get<any>(SpotlyteService.endpoints.results(jobId)).pipe(
            map(mapJobResults),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    createJob(request: ICreateJobRequest): Observable<ISpotlyteJob> {
        const ERROR = 'Failed to create job';
        return this.http.post<any>(SpotlyteService.endpoints.jobs, mapCreateJobRequest(request)).pipe(
            map(mapSpotlyteJob),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    cancelJobs(ids: string[]): Observable<ISpotlyteJob[]> {
        const ERROR = 'Failed to cancel jobs';
        return this.http.delete<any[]>(SpotlyteService.endpoints.jobs, { body: { ids } }).pipe(
            map(r => r.map(mapSpotlyteJob)),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    getFindings(filters: IFindingsRequest): Observable<IFindingsPage> {
        const ERROR = 'Failed to load findings';
        return this.http.post<any>(SpotlyteService.endpoints.findings, mapFindingsRequest(filters)).pipe(
            map(mapFindingsPage),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    getQnaHistory(jobId: string): Observable<ISpotlyteQnaTurn[]> {
        const ERROR = `Failed to load QnA history for job ${jobId}`;
        return this.http.get<any[]>(SpotlyteService.endpoints.qnaHistory(jobId)).pipe(
            map(r => r.map(mapSpotlyteQnaTurn)),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    askQuestion(jobId: string, question: string): Observable<ISpotlyteQnaTurn> {
        const ERROR = 'Failed to submit question';
        return this.http.post<any>(SpotlyteService.endpoints.qna, { job_id: jobId, question }).pipe(
            map(mapSpotlyteQnaTurn),
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    exportCsv(jobId: string): Observable<Blob> {
        const ERROR = `Failed to export CSV for job ${jobId}`;
        return this.http.get(SpotlyteService.endpoints.exportCsv(jobId), { responseType: 'blob' as const }).pipe(
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    exportPdf(jobId: string): Observable<Blob> {
        const ERROR = `Failed to export PDF for job ${jobId}`;
        return this.http.get(SpotlyteService.endpoints.exportPdf(jobId), { responseType: 'blob' as const }).pipe(
            catchError(e => { console.error(ERROR, e); return throwError(() => new Error(ERROR)); }),
        );
    }

    getSummaryBlob(url: string): Observable<ISummary> {
        return this.fetchCachedBlob(url, mapSummary);
    }

    getChartsBlob(url: string): Observable<ICharts> {
        return this.fetchCachedBlob(url, mapCharts);
    }

    getFindingsBlob(url: string): Observable<IFinding[]> {
        return this.fetchCachedBlob(url, (raw: any) => Array.isArray(raw) ? raw.map(mapFinding) : []);
    }

    getZonesBlob(url: string): Observable<IZone[]> {
        return this.fetchCachedBlob(url, mapZones);
    }

    private fetchCachedBlob<T>(url: string, mapper: (r: any) => T): Observable<T> {
        if (!this.blobCache.has(url)) {
            this.blobCache.set(url,
                this.http.get<any>(url, { context: SpotlyteService.PUBLIC_CONTEXT }).pipe(
                    map(mapper),
                    shareReplay(1),
                ),
            );
        }
        return this.blobCache.get(url)!;
    }
}
